/**
 * `DimensionBars` — one horizontal bar per row (a soil dimension, a watershed, a practice
 * group), each with an optional reference tick and a flagged/neutral fill. Hand-built SVG
 * in the same register as `RankedBarChart`/`HistogramChart` (the repo forbids chart
 * libraries): viewBox-scaled, `role="img"` with a full plain-text readout carrying every
 * number with its own unit and period.
 *
 * ## Layout round, 2 October 2026 (client: "crowded, not organised")
 *
 * The chart used to spend **two lines on every row** — the label alone at `x = 0`, the bar
 * under it, the value far right — which made an eleven-watershed ranking a full screen of
 * the 450 px dock and left a ragged left edge where the labels and the bars started at the
 * same x but meant different things.
 *
 * One row is now **one line**: a fixed `DB_LABEL_W` label column (truncated with an
 * ellipsis, the full text in the row's own `<title>`), the bar track, and the value
 * right-aligned in a `DB_VALUE_W` column. Row pitch is `DB_ROW_PITCH`, so eleven rows are
 * a block a reader takes in at once rather than a scroll.
 *
 * **Two lines survive for one case only**: a row carrying a `note` (UC1's mixed-unit
 * constraints name the metric they answered from). Then every row gets the taller pitch
 * and the note prints under its own bar — the alternative was a note squeezed into the
 * value column, which is what pushed the value off the viewBox before.
 *
 * The **`ref` caption is a legend entry, not a per-row label**: it was one fact about the
 * chart printed under every tick, eleven times in the same chart. `refCaption=""` keeps
 * the tick and prints no legend either, for a caller that legends it its own way
 * (`Uc2PairCard`).
 *
 * The bar scale is a **domain**, not a maximum: `[min(0, values, refs), max(0, values,
 * refs)]`, with a zero baseline drawn whenever the domain reaches below zero, so a negative
 * value (a falling rainfall trend, say) grows left from the baseline instead of vanishing.
 *
 * Scaling round, 3 October 2026: a row may be a **token** instead of a measurement — a
 * fixed-width chip at the baseline, out of the domain, printed as its own `valueText` and
 * hatchable. UC2's greening check is a pass or a fail, and the focused drivers block has
 * to draw it beside the three measured rows or the sentence under the chart talks about a
 * leg the chart does not have.
 */
import { useId } from 'react';

import { fmtNum } from '../../../lib/format';
// The one neutral grey, from `uc1Constants.ts` — see the note in `UnitMatrix.tsx`.
import { NEUTRAL_COLOR } from '../uc1Constants';
import { LABEL_CHAR_W, LABEL_COL_W, clipLabel } from './labels';

/** Bar height. */
export const DB_ROW_H = 12;
/** Full row pitch, one line per row. */
export const DB_ROW_PITCH = 22;
/** Row pitch while any row carries a note — the note is a second line under the bar. */
export const DB_NOTE_ROW_PITCH = 34;
/** Chart width, matched to the Analysis dock's own card body at 450 px. */
export const DB_W = 394;
/** Left-hand column reserved for the row label. */
export const DB_LABEL_W = LABEL_COL_W;
/** Gap between the label column and the bar track. */
export const DB_LABEL_GAP = 8;
/** Right-hand column reserved for the value text. */
export const DB_VALUE_W = 64;
/** Gap between the bar track's end and the value column. */
export const DB_VALUE_PAD = 8;
/** Bar track width at the nominal value column — the widest a bar can ever be. */
export const DB_BAR_W = DB_W - DB_LABEL_W - DB_LABEL_GAP - DB_VALUE_W - DB_VALUE_PAD;
export const DB_PAD_TOP = 4;
export const DB_MIN_H = 30;
/** The verdict token's width — wide enough to carry a colour, too short to read as a value. */
export const DB_TOKEN_W = 18;

const FLAGGED_COLOR = 'var(--brand)';

/** Left edge of the bar track. */
const plotX = DB_LABEL_W + DB_LABEL_GAP;
/** The value column is right-aligned on the chart's own right edge. */
const valueX = DB_W;

/**
 * The value column is `DB_VALUE_W` **or wider**, whichever the longest value text needs.
 *
 * 64 px holds "140.5 %" and "14.1 g/kg", which is every chart in UC1 and most of UC2. It
 * does not hold "0.0176 per year" — the greening ranking, where a fixed column had the
 * longest bars running straight under the digits. The track gives up the difference, so
 * the three columns still line up down the chart and nothing overlaps.
 */
function valueColumnW(rows: DimRow[]): number {
  const longest = rows.reduce((n, row) => Math.max(n, formatValue(row).length), 0);
  return Math.max(DB_VALUE_W, Math.min(DB_W / 2, longest * LABEL_CHAR_W + 4));
}

export interface DimRow {
  id: string;
  label: string;
  value: number | null;
  unit?: string;
  reference?: number | null;
  flagged?: boolean;
  period?: string;
  note?: string;
  color?: string;
  /**
   * A **verdict token**, not a measurement: a fixed-width chip at the baseline, left out
   * of the chart's domain and printed as `valueText` rather than as a number. UC2's
   * greening check is pass / fail / nothing to judge, and giving it a bar length would be
   * a number the run never served; leaving it off the chart entirely would be a sentence
   * talking about a leg the picture does not draw.
   */
  token?: boolean;
  /** What the value column prints instead of the measured number. */
  valueText?: string;
  /** Hatch over the bar — the chart's "nothing to judge here". */
  hatched?: boolean;
  /** Decimal places for this row's value and reference text (default `1`) — a row whose
   * native precision needs more than one decimal (e.g. a small per-year slope) passes its
   * own `decimals` rather than every row in the chart being forced onto the same rounding. */
  decimals?: number;
}

export interface DimensionBarsProps {
  rows: DimRow[];
  max?: number;
  ariaLabel: string;
  onSelect?: (id: string) => void;
  selectedId?: string | null;
  /**
   * `'shared'` (default): every row's bar and reference tick are positioned against one
   * domain (`[min(0, …), max ?? max(0, …)]` over every row's value and reference) — fine
   * when every row shares a unit. `'row'`: each row gets its own domain, padded by 1.2 —
   * needed when rows mix units (%, mm, t/ha, ...), so a small-magnitude row's bar isn't a
   * sliver next to a large-magnitude one. A row-mode row with no numeric `reference` draws
   * **no bar at all**: with nothing to read it against, a per-row bar length carries no
   * information, so only the value text is printed.
   */
  scale?: 'shared' | 'row';
  /** Word for a reference in the plain-text readout and the legend (default `'reference'`). */
  referenceLabel?: string;
  /**
   * Whether the reference tick is legended under the chart at all. **Empty prints none** —
   * for a chart whose rows all read against the *same* reference (UC2's two-row pair
   * card), where the caller legends it its own way. Any non-empty value legends the tick
   * once, under the chart, with `referenceLabel` as its wording.
   */
  refCaption?: string;
}

interface Domain {
  lo: number;
  hi: number;
}

/** `span` never reaches zero, so no position divides by nothing. */
function span(d: Domain): number {
  return d.hi - d.lo || 1;
}

/** The chart-wide domain: every measured row's value and reference, always including zero.
 * A token row is not a measurement and must not stretch the scale the others are read on. */
function sharedDomain(rows: DimRow[], max?: number): Domain {
  const values: number[] = [0];
  for (const r of rows) {
    if (r.token) continue;
    if (typeof r.value === 'number') values.push(r.value);
    if (typeof r.reference === 'number') values.push(r.reference);
  }
  return { lo: Math.min(...values), hi: max != null ? max : Math.max(...values) };
}

/** `scale="row"`'s own per-row domain: zero, the value and the reference, padded by 1.2 on
 * whichever sides are non-zero so the longest bar never touches the track's end. */
function rowDomain(row: DimRow): Domain {
  const values: number[] = [0];
  if (typeof row.value === 'number') values.push(row.value);
  if (typeof row.reference === 'number') values.push(row.reference);
  const lo = Math.min(...values);
  const hi = Math.max(...values);
  return { lo: lo < 0 ? lo * 1.2 : 0, hi: hi > 0 ? hi * 1.2 : 0 };
}

/** A value's own x inside the track, clamped to the track. */
function posOf(v: number, d: Domain, barW: number): number {
  const frac = (v - d.lo) / span(d);
  return plotX + Math.min(1, Math.max(0, frac)) * barW;
}

/** One row's value text: `fmtNum` + unit, or the em dash for `null` — shared by the readout
 * and the JSX, like `RankedBarChart`'s own `withUnit` helper. */
function formatValue(row: DimRow): string {
  if (row.valueText != null) return row.valueText;
  const unit = row.unit ? ` ${row.unit}` : '';
  return row.value == null ? '—' : `${fmtNum(row.value, row.decimals ?? 1)}${unit}`;
}

/** The chart's own `aria-label` — a plain-text readout of every row, for §I5 and the test list. */
function readout(ariaLabel: string, rows: DimRow[], referenceLabel: string): string {
  const parts = rows.map((row) => {
    const unit = row.unit ? ` ${row.unit}` : '';
    const period = row.period ? ` (${row.period})` : '';
    const valueText = formatValue(row);
    const refText =
      row.reference != null
        ? `, ${referenceLabel} ${fmtNum(row.reference, row.decimals ?? 1)}${unit}`
        : '';
    const noteText = row.note ? ` — ${row.note}` : '';
    return `${row.label} ${valueText}${period}${refText}${noteText}`;
  });
  return `${ariaLabel} — ${parts.join('. ')}`;
}

export function DimensionBars({
  rows,
  max,
  ariaLabel,
  onSelect,
  selectedId,
  scale = 'shared',
  referenceLabel = 'reference',
  refCaption = 'ref',
}: DimensionBarsProps) {
  // One line per row, unless a row carries a note — then the note is its second line and
  // every row takes the taller pitch so the rhythm stays even down the chart.
  const twoLine = rows.some((row) => Boolean(row.note));
  const pitch = twoLine ? DB_NOTE_ROW_PITCH : DB_ROW_PITCH;
  const h = Math.max(DB_MIN_H, DB_PAD_TOP * 2 + Math.max(rows.length, 1) * pitch);
  const shared = sharedDomain(rows, max);
  const barW = DB_W - plotX - valueColumnW(rows) - DB_VALUE_PAD;
  const hasReference = rows.some((row) => typeof row.reference === 'number');
  const rawId = useId();
  /** `useId` spells its ids with colons; a `url(#…)` reference wants none. */
  const hatchId = `dim-hatch-${rawId.replace(/[^A-Za-z0-9_-]/g, '')}`;
  const anyHatched = rows.some((row) => row.hatched);

  return (
    <>
      <svg
        className="intel-chart dim-bars"
        viewBox={`0 0 ${DB_W} ${h}`}
        role="img"
        aria-label={readout(ariaLabel, rows, referenceLabel)}
        data-testid="dimension-bars-chart"
      >
        {anyHatched ? (
          <defs>
            <pattern
              id={hatchId}
              width={4}
              height={4}
              patternUnits="userSpaceOnUse"
              patternTransform="rotate(45)"
            >
              <line x1={0} y1={0} x2={0} y2={4} stroke="#fff" strokeWidth={1.6} />
            </pattern>
          </defs>
        ) : null}
        {rows.map((row, i) => {
          const rowTop = DB_PAD_TOP + i * pitch;
          const barY = rowTop + (DB_ROW_PITCH - DB_ROW_H) / 2;
          const midY = barY + DB_ROW_H / 2 + 3.5;
          const barColor = row.color ?? (row.flagged ? FLAGGED_COLOR : NEUTRAL_COLOR);
          const domain = scale === 'row' ? rowDomain(row) : shared;
          // Row mode with nothing to read the bar against: the length would be an artefact
          // of the row's own padding, identical for every such row, so no bar is drawn.
          const unreadable = scale === 'row' && typeof row.reference !== 'number';
          const zeroX = posOf(0, domain, barW);
          const endX = row.value != null ? posOf(row.value, domain, barW) : zeroX;
          const w = row.token
            ? DB_TOKEN_W
            : row.value == null || unreadable
              ? 0
              : Math.max(2, Math.abs(endX - zeroX));
          const barX = !row.token && endX < zeroX ? Math.min(endX, zeroX - w) : zeroX;
          const valueText = formatValue(row);
          const refX =
            row.reference != null && !unreadable && !row.token
              ? posOf(row.reference, domain, barW)
              : null;
          const selected = row.id === selectedId;

          return (
            <g
              key={row.id}
              className="dim-row"
              data-row={row.id}
              style={onSelect ? { cursor: 'pointer' } : undefined}
              onClick={onSelect ? () => onSelect(row.id) : undefined}
            >
              <title>{`${row.label} · ${valueText}`}</title>
              <text
                className="dim-rowlabel"
                x={0}
                y={midY}
                textAnchor="start"
                /* The focused row is bold as well as outlined: an outline on a two-pixel
                   bar is easy to miss, and the name is what a reader scans for. */
                fontWeight={selected ? 700 : undefined}
              >
                {clipLabel(row.label)}
              </text>
              {domain.lo < 0 && !unreadable && !row.token ? (
                <line
                  data-zero=""
                  x1={zeroX}
                  x2={zeroX}
                  y1={barY - 1}
                  y2={barY + DB_ROW_H + 1}
                  stroke="var(--ink-3)"
                  strokeWidth={1}
                />
              ) : null}
              {w > 0 ? (
                <rect
                  className={row.flagged ? 'bar flagged' : 'bar'}
                  data-token={row.token ? '' : undefined}
                  x={barX}
                  y={barY}
                  width={w}
                  height={DB_ROW_H}
                  rx={3}
                  fill={barColor}
                  opacity={selected ? 1 : 0.85}
                  stroke={selected ? 'var(--ink)' : 'none'}
                  strokeWidth={selected ? 1.5 : 0}
                />
              ) : null}
              {w > 0 && row.hatched ? (
                <rect
                  data-hatched=""
                  x={barX}
                  y={barY}
                  width={w}
                  height={DB_ROW_H}
                  rx={3}
                  fill={`url(#${hatchId})`}
                />
              ) : null}
              {refX != null ? (
                <line
                  data-ref=""
                  x1={refX}
                  x2={refX}
                  y1={barY - 2}
                  y2={barY + DB_ROW_H + 2}
                  stroke="var(--ink-3)"
                  strokeWidth={1.5}
                  strokeDasharray="2,2"
                />
              ) : null}
              <text className="dim-num" x={valueX} y={midY} textAnchor="end">
                {valueText}
              </text>
              {row.note ? (
                <text className="dim-note" x={plotX} y={barY + DB_ROW_H + 11}>
                  {row.note}
                </text>
              ) : null}
            </g>
          );
        })}
      </svg>
      {hasReference && refCaption ? (
        <ul className="chart-legend" data-testid="dim-ref-legend">
          <li>
            <span className="swatch dim-swatch-ref" aria-hidden="true" />
            <span className="legend-label">{referenceLabel}</span>
          </li>
        </ul>
      ) : null}
    </>
  );
}

export default DimensionBars;
